<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>

          <v-form @submit.prevent="search" ref="formRefs">
            <v-row>
              <v-col cols="3">
                <ZoneMultiSelect
                  v-model="searchCondition.zoneCodeList"
                  :items="zoneItem"
                  display-mode="code"
                  :label="$t('zaikoJanList.label.plantCode')"
                />
              </v-col>
              <v-col cols="3">
                <ZoneMultiSelect
                  v-model="searchCondition.zoneCodeList"
                  :items="zoneItem"
                  display-mode="name"
                  :label="$t('zaikoJanList.label.zoneName')"
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  density="compact"
                  variant="outlined"
                  hide-details="auto"
                  v-model="searchCondition.typeCode"
                  :label="$t('zaikoJanList.label.typeCode')"
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  density="compact"
                  variant="outlined"
                  hide-details="auto"
                  v-model="searchCondition.sizeName"
                  :label="$t('zaikoJanList.label.sizeName')"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <v-text-field
                  density="compact"
                  variant="outlined"
                  hide-details="auto"
                  v-model="searchCondition.productName"
                  :label="$t('zaikoJanList.label.productName')"
                />
              </v-col>
              <v-col cols="3">
                <v-select
                  density="compact"
                  variant="outlined"
                  hide-details="auto"
                  v-model="searchCondition.brandCode"
                  :items="brandItem"
                  :label="$t('zaikoJanList.label.brandCode')"
                  clearable
                />
              </v-col>
              <v-col cols="3" class="d-flex align-center">
                <v-checkbox
                  density="compact"
                  hide-details="auto"
                  v-model="searchCondition.excludeZeroStock"
                  :label="$t('zaikoJanList.label.excludeZeroStock')"
                />
              </v-col>
              <!-- ダッシュボードから遷移した場合の店舗条件(画面上で確認・解除できるように表示する) -->
              <v-col cols="3" class="d-flex align-center" v-if="searchCondition.shopCodeList?.length">
                <v-chip
                  v-for="shopCode in searchCondition.shopCodeList"
                  :key="shopCode"
                  closable
                  @click:close="removeShopCode(shopCode)"
                >{{ $t('zaikoJanList.label.shopCodeList') }}: {{ shopLabel(shopCode) }}</v-chip>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="text-right">
                <v-btn text @click="clear">{{ $t('common.action.clear') }}</v-btn>
                <v-btn
                  theme="dark"
                  class="ml-4 px-8 bg-grey-darken-3"
                  elevation="2"
                  type="submit"
                  :loading="isSearching"
                >{{ $t('common.action.search') }}</v-btn
                >
              </v-col>
            </v-row>
          </v-form>

          <v-row>
            <v-col cols="12">
              <v-table density="compact" class="text-no-wrap resizable-table">
                <thead>
                  <tr>
                    <th v-for="header in headers" :key="header">
                      <div class="header-title" @click.stop="tableSort(header)" :style="cursorCheck(header)">
                        {{ $t("zaikoJanList.label.table." + header) }}
                      </div>
                      <div class="resize-handle" @mousedown="resizeColumnWidth"></div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="data in filteredDataList" :key="data.no" @click="rowClick(data)" :style="cursorCheck(null)">
                    <td v-for="header in headers" :key="header">{{ data[header] }}</td>
                  </tr>
                  <tr>
                    <td :colspan="headers.length"></td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="3"></v-col>
            <v-col cols="6">
              <v-pagination 
                v-model="currentPageNo"
                :length="maxPageNo"/>
            </v-col>
            <v-col cols="3">
              <v-row>
                <v-spacer></v-spacer>
                <v-col cols="10" class="d-flex justify-end">
                  <v-select
                    density="compact"
                    variant="outlined"
                    hide-details="auto"
                    v-model="displayPerPage"
                    :items="displayPerPageItem"
                    item-title="name"
                    item-value="value"
                    :label="$t('common.label.displayCountPerPage')"
                    @update:modelValue = "resetCondition()"
                    return-object/>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" class="text-right">
                  {{displayContentNo.minNo}} - {{displayContentNo.maxNo}}{{ $t('common.label.displayedDataRange') }} / {{ dataList.length }}{{ $t('common.label.totalCountOf') }}
                </v-col>
              </v-row>

            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="text-right">
              <v-btn
                dark
                class="px-8 bg-indigo-darken-1"
                elevation="2"
                @click="downloadCsv"
                :disabled="dataList.length == 0"
                >{{ $t('common.action.outputCsv') }}</v-btn
              >
            </v-col>
          </v-row>
        </v-container>
        <ZeroData v-if="zeroDataWarningDialogFlg" :handle-ok="closeZeroDataDialog"/>
        <MessageDialog v-if="commonMsgDlgVisible" :dialog-data="dataForMsgDialog" :handle-ok="closeMessageDataDialog" />
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script lang="js">
import Vue from "vue";
import AppWrapperView from "@/components/views/AppWrapperView";
import api from "@/apis/staff";
import store from "@/store";
import utils from "@/utils";
import router from "@/router/index";
import Paths from "@/router/indexPaths";
import ZeroData from "@/components/dialog/ZeroDataDialog";
import MessageDialog from "@/components/dialog/MessageDialog";
import tableUtils from "@/utils/tableUtils";
import ZoneMultiSelect from "@/components/ZoneMultiSelect";

export default {
  components: {AppWrapperView, ZeroData, MessageDialog, ZoneMultiSelect},
  props: {},
  computed: {
    //どの部分を表示しているのか
    displayContentNo(){
      const minNo = this.dataList.length != 0 ? 1 + (this.displayPerPage.value * (this.currentPageNo - 1)) : 0;
      const maxNo = this.displayPerPage.value * (this.currentPageNo);
      if(maxNo > this.dataList.length || this.displayPerPage.value == 0){
        return {minNo: minNo, maxNo: this.dataList.length};
      }
      return {minNo: minNo, maxNo: maxNo};
    },

    //最大ページ
    maxPageNo() {
      if(this.displayPerPage.value == 0) return 0;
      return Math.ceil(this.dataList.length/this.displayPerPage.value);
    },

    //ソート&日付変換後のデータ
    sortedTable() {
      let tempArray = this.dataList.map(x => x);
      if(this.sortState.target != ""){
        //昇順
        if(this.sortState.ascent) tempArray = tempArray.sort((a, b) => {
          if(a[this.sortState.target] > b[this.sortState.target]){
            return 1;
          }
          if(a[this.sortState.target] < b[this.sortState.target]){
            return -1;
          }
          return 0;
        })
        //降順
        else tempArray = tempArray.sort((a, b) => {
          if(a[this.sortState.target] > b[this.sortState.target]){
            return -1;
          }
          if(a[this.sortState.target] < b[this.sortState.target]){
            return 1;
          }
          return 0;
        })
      }

      // 変換
      tempArray = tempArray.map((x, index) => {
        // noはなし
        // x.no = index + 1;
        x.rfidIntroduced = utils.replaceEpcFlagWord(x.rfidIntroduced);
        x.productRegisteredDatetime = utils.putSplitWordToCalendarWithTime(x.productRegisteredDatetime);
        x.productUpdatedDatetime = utils.putSplitWordToCalendarWithTime(x.productUpdatedDatetime);
        return x;
      })

      return tempArray;
    },

    // ページ区切り後のデータ
    filteredDataList() {
      const tempArray = this.sortedTable;
      return tempArray.slice(this.displayContentNo.minNo-1, this.displayContentNo.maxNo);
    },

    //ゾーンリスト(plantコード/店舗名)
    // 全店舗のゾーンをロケコードで重複排除して返す
    zoneItem() {
      const zoneMap = new Map();
      this.shopItem.forEach(shop => {
        (shop.zoneList ?? []).forEach(zone => {
          if (!zoneMap.has(zone.code)) zoneMap.set(zone.code, {code: zone.code, name: zone.name});
        });
      });
      return [...zoneMap.values()];
    },
    // ヘッダ
    headers() {
      return this.$getTableDef("ZaikoJanListPage");
    },

    rfidIntroducedItems() {
    return [
      {key: 'true', label: utils.replaceEpcFlagWord('true')},
      {key: 'false', label: utils.replaceEpcFlagWord('false')}
    ];
    }
  },

  data: () => ({
    currentPageNo: 1,
    prevRoutePath: "",

    //１ページ表示数
    displayPerPage: {},
    displayPerPageItem: [],

    //ソートの状態
    sortState: {target: "", ascent: true},

    // 店の中にゾーンも含まれている
    // ゾーンはcomputedで別計算
    shopItem: [],
    // ジャンル①リスト
    brandItem: [],

    dataList: [],
    searchCondition: {
      shopCodeList: [],       // 店舗コードリスト
      janCode: "",            // JANコード
      zoneCodeList: [],           // ロケ(ゾーンCD)
      typeCode: "",           // 品番CD
      colorCode: "",          // カラーCD
      sizeCode: "",           // サイズCD
      brandCode: null,        //ブランドCD(ジャンル①)
      productName: "",        // 品名
      makerName: "",          // メーカー名
      brandName: "",          // ブランド名
      colorName: "",          // カラー名称
      sizeName: "",           // サイズ名称
      stockQuantity: "",      // 在庫数
      excludeZeroStock: true, // 在庫数0を表示しない
      rfidIntroduced: null      // RFID対象
    },
    // 0件取得時の警告ダイアログ
    zeroDataWarningDialogFlg: false,
    // 検索中
    isSearching: false,
    // 汎用メッセージダイアログ
    commonMsgDlgVisible: false,
    dataForMsgDialog: {
      message: "",
      errorList: [],
    },
  }),
  
  methods: {
    //数値以外の入力を不可
    restrictToNumbers(event, fieldName) {
      const val = event.target.value;
      let numericValue = val.replace(/[^0-9]/g, '');
      if (numericValue.length > 9) {
        numericValue = numericValue.slice(0, 9);
      }
      this.searchCondition[fieldName] = numericValue;
    },

    //カーソルの変更
    cursorCheck(value){
      return "cursor: pointer;"
    },

    // 店舗リストの取得
    // 自店舗検索リスト取得
    // selectSiteFlag：0：他店舗含む / 1：自店舗のみ
    getValidShopSearchList(){
      api.post("/store/search", {selectSiteFlag: 0})
      .then((response)=>{
        if(response.status == 1) return; //do nothing
        response.data.results?.shopList.forEach(element => {
          this.shopItem.push({name: element.name, code: element.code, zoneList: element.zoneList, code_name: element.code+'_'+element.name}) 
        })
        if (this.isNavigatedFromDashboardPanel()) {
          this.searchCondition.shopCodeList = [store.getDashboardSiteCode()];
          return;
        }
      })
      .catch((error) => {
        //do nothing
      })
    },

    // ジャンル①(ブランドCD)リストの取得
    getBrandList() {
      api.post("/product/brand/list", {})
      .then((response)=>{
        this.brandItem = response.data.results?.brandList ?? [];
      })
      .catch((error) => {
        //do nothing
      })
    },

    // 店舗条件の表示名
    shopLabel(shopCode) {
      return this.shopItem.find(x => x.code == shopCode)?.code_name ?? shopCode;
    },

    // 店舗条件の解除
    removeShopCode(shopCode) {
      this.searchCondition.shopCodeList = this.searchCondition.shopCodeList.filter(x => x != shopCode);
    },

    //テーブルの条件リセット
    resetCondition(){
      //１ページ目
      this.currentPageNo = 1;
      //ソートなし
      this.sortState.target = "";
      this.sortState.ascent = true;
    },
    tableSort(headValue) {
      if(headValue == "no") return;
      this.currentPageNo = 1;
      if(this.sortState.target == headValue){
        this.sortState.ascent = !this.sortState.ascent;
      }
      else{
        this.sortState.target = headValue;
        this.sortState.ascent = true;
      }
    },

    rowClick(item, row) {
      store.setZaikoJanCode(item.janCode);
      store.setZaikoShopCode(item.shopCode);
      store.setZaikoJanListTableState(JSON.stringify({
        currentPageNo: this.currentPageNo,
        sortState: this.sortState,
        displayPerPageValue: this.displayPerPage.value,
      }));
      router.push({name:Paths.ZAIKO_EPC_LIST})
    },

    clear() {
      this.searchCondition = {
        shopCodeList: [],       // 店舗コード
        janCode: "",            // JANコード
        zoneCodeList: [],           // ロケ(ゾーンCD)
        typeCode: "",           // 品番CD
        colorCode: "",          // カラーCD
        sizeCode: "",           // サイズCD
        brandCode: null,        //ブランドCD(ジャンル①)
        productName: "",        // 品名
        makerName: "",          // メーカー名
        brandName: "",          // ブランド名
        colorName: "",          // カラー名称
        sizeName: "",           // サイズ名称
        stockQuantity: "",      // 在庫数
        excludeZeroStock: true, // 在庫数0を表示しない
        rfidIntroduced: null      // RFID対象
      }
    },

    closeZeroDataDialog() {
      this.zeroDataWarningDialogFlg = false;
    },

    async search() {
      this.isSearching = true;
      try {
        store.setZaikoJanListSearchCondition(JSON.stringify(this.searchCondition));
      } catch (e) {
        // do nothing
      }
      api.post("/zaiko/search", {
          shopCodeList: this.searchCondition.shopCodeList ?? [],    // 店舗CD
          janCode: this.searchCondition.janCode,                    // JANコード
          zoneCodeList: this.searchCondition.zoneCodeList ?? [],            // ロケ
          productCode: this.searchCondition.typeCode,                  // 品番CD
          colorCode: this.searchCondition.colorCode,                // カラーCD
          sizeCode: this.searchCondition.sizeCode,                  // サイズCD
          brandCode: this.searchCondition.brandCode,  //ブランドCD
          shopName: this.searchCondition.shopName,           // 店舗名
          productName: this.searchCondition.productName,     // 品名
          makerName: this.searchCondition.makerName,         // メーカー名
          brandName: this.searchCondition.brandName,         // ブランド名
          colorName: this.searchCondition.colorName,         // カラー名称
          sizeName: this.searchCondition.sizeName,           // サイズ名称
          stockQuantity: this.searchCondition.stockQuantity, // 在庫数
          excludeZeroStock: this.searchCondition.excludeZeroStock, // 在庫数0を表示しない
          rfidIntroduced: this.searchCondition.rfidIntroduced // RFID対応
        }
      )
      .then((response)=>{
        this.dataList = response.data.results?.zaikoList ?? [];
        this.resetCondition();

        // 0件ダイアログ処理
        if(this.dataList.length == 0) this.zeroDataWarningDialogFlg = true;
        this.isSearching = false;

        // 検索件数上限超過
        if (response.data.results?.isExceededMaxSearchCount) {
          this.dataForMsgDialog = {};
          this.dataForMsgDialog.errorList = [];
          this.dataForMsgDialog.message = this.$t('common.message.exceededMaxSearchCount');
          this.commonMsgDlgVisible = true;
        }
        this.restoreTableState();
        tableUtils.adjustColumnWidth(tableUtils.getSearchResultTable());
      })
      .catch((error) => {
        // 0件 dialog
        this.dataList = [];
        this.zeroDataWarningDialogFlg = true;
        this.isSearching = false;
      })
    },
    resizeColumnWidth: tableUtils.resizeColumnWidth,
    restoreTableState() {
      // EPC一覧からの戻り時のみテーブル状態を復元
      if (this.prevRoutePath != Paths.ZAIKO_EPC_LIST) return;
      const tableStateJson = store.getZaikoJanListTableState();
      if (!tableStateJson) return;
      store.removeZaikoJanListTableState();
      const tableState = JSON.parse(tableStateJson);
      this.sortState = tableState.sortState;
      const matchedDisplay = this.displayPerPageItem.find(e => e.value === tableState.displayPerPageValue);
      if (matchedDisplay) this.displayPerPage = matchedDisplay;
      // 復元したページが現在の最大ページを超える場合は最終ページに移動する
      this.currentPageNo = Math.min(tableState.currentPageNo, Math.max(this.maxPageNo, 1));
    },

    downloadCsv() {
      utils.simpleDownloadCsv(this.headers, this.sortedTable, "zaikoJanList.label.table.", 'zaikoJanList.fileNamePrefix.csv', this.$t);
    },
    initFromDashboard() {
      this.searchCondition.shopCodeList = [store.getDashboardSiteCode()];
      this.search();
    },
    initFromEpcList() {
      this.searchCondition = JSON.parse(store.getZaikoJanListSearchCondition());
      this.search();
    },
    isNavigatedFromEpcList() {
      return this.prevRoutePath == Paths.ZAIKO_EPC_LIST;
    },
    isNavigatedFromDashboardPanel() {
      // NOTE
      // "遷移元がダッシュボード"という条件だけだと、メニューのリンクを押下しても遷移イベントが発生する。
      // パネル押下時にセッションに店舗コードを設定するので、それの有無でパネルからの遷移を判断する。
      return this.prevRoutePath == Paths.DASHBOARD && store.getDashboardSiteCode();
    },
    // 共通メッセージダイアログを閉じる
    closeMessageDataDialog() {
      this.commonMsgDlgVisible = false;
    },
    /** 1ページ当たりの表示件数のリストを取得 */
    getDisplayPerPageItem() {
      const unitOfRecord = this.$t('common.label.unitOfRecord');
      return [
        { name: "50" + unitOfRecord, value: 50 },
        { name: "100" + unitOfRecord, value: 100 },
        { name: "500" + unitOfRecord, value: 500 },
        { name: "1000" + unitOfRecord, value: 1000 },
      ];
    },
  },
  created() {
    this.getValidShopSearchList()
    this.getBrandList()
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoutePath = from?.path;
    })
  },
  mounted() {
    this.displayPerPageItem = this.getDisplayPerPageItem();
    this.displayPerPage = this.displayPerPageItem[0];

    if (this.isNavigatedFromEpcList()) {
      this.initFromEpcList();
      return;
    }
    if (this.isNavigatedFromDashboardPanel()) {
      this.initFromDashboard();
      return;
    }
  },
};
</script>

<style>

</style>
